import React, { useRef, useEffect, useCallback } from 'react'
import { IconAlertTriangle } from '@tabler/icons-react'

const IDLE_MONITOR_INTERVAL = 1 * 60 * 1000 // 1 minute
const SESSION_TIMEOUT_DURATION = 15 * 60 * 1000 // 15 minutes
const PROMPT_TIMEOUT_DURATION = 14 * 60 * 1000 // 14 minutes
const SESSION_UPDATE = 'session_update'
const SESSION_LOGOUT = 'session_logout'

const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'visibilitychange']

const logger = (message, type = 'IDLE MONITOR', silent = false) => {
  if (!silent) {
    console.info(`[${type}]: ${message}`)
  }
}

export const useIdleMonitor = (
  userId,
  sessionBroadcast,
  onSessionExpired,
  onPrompt,
  enableIdleMonitor = false
) => {
  const sessionTimeout = useRef(null)
  const lastActiveRef = useRef(null)
  const expirationRef = useRef(null)
  const promptRef = useRef(null)

  const clearTimestamps = () => {
    lastActiveRef.current = null
    expirationRef.current = null
    promptRef.current = null
  }

  const createTimestamps = () => {
    const now = Date.now()
    lastActiveRef.current = now
    expirationRef.current = now + SESSION_TIMEOUT_DURATION
    promptRef.current = now + PROMPT_TIMEOUT_DURATION
  }

  const updateTimestamps = (timestamps) => {
    lastActiveRef.current = timestamps.lastActive
    expirationRef.current = timestamps.expiration
    promptRef.current = timestamps.prompt
  }

  const getTimestamps = () => {
    const lastActive = lastActiveRef.current
    const expiration = expirationRef.current
    const prompt = promptRef.current

    return { lastActive, expiration, prompt }
  }

  const clearPrompt = () => {
    onPrompt(null)
  }

  const clearSession = () => {
    clearTimeout(sessionTimeout.current)
    clearTimestamps()
  }

  const handleUpdateIdleTimer = useCallback(() => {
    createTimestamps()
  })

  const setEvents = () => {
    events.forEach((event) => {
      window.addEventListener(event, handleUpdateIdleTimer, { passive: true })
    })
  }

  const clearEvents = () => {
    events.forEach((event) => {
      window.removeEventListener(event, handleUpdateIdleTimer, { passive: true })
    })
  }

  const clearAll = () => {
    clearPrompt()
    clearSession()
    clearEvents()

    if (sessionBroadcast.broadcastChannel) {
      sessionBroadcast.close()
    }
  }

  const handlePrompt = () => {
    logger(`Session for user: ${userId} is about to expire, prompting user`)

    onPrompt({
      header: 'Your session will expire soon',
      content:
        "You'll be logged out in 1 minute due to inactivity. To stay signed in, continue using the app.",
      warning: true,
      icon: <IconAlertTriangle />,
    })
  }

  const handleUpdateSession = (timestamps) => {
    clearPrompt()

    // Incoming data from another tab
    if (timestamps) {
      logger(
        `Session for user: ${userId} has been inactive for ${Math.floor((Date.now() - timestamps.lastActive) / 1000)} seconds.`
      )
      updateTimestamps(timestamps)
    } else {
      logger(
        `Session for user: ${userId} has been inactive for ${Math.floor((Date.now() - lastActiveRef.current) / 1000)} seconds.`
      )
    }
  }

  const handleExpiredSession = () => {
    logger(`Session expired, logging out user: ${userId}`)
    clearAll()

    onSessionExpired()
  }

  const startTimer = () => {
    // Clear anything that might be running just in case
    clearPrompt()
    clearSession()
    clearEvents()

    // Setup
    createTimestamps()
    setEvents()

    sessionTimeout.current = setInterval(() => {
      const timestamps = getTimestamps()
      const now = Date.now()
      const isExpired = now > timestamps.expiration
      const shouldShowPrompt = !isExpired && now >= timestamps.prompt

      if (isExpired) {
        sessionBroadcast.broadcastChannel.postMessage({ type: SESSION_LOGOUT })
        handleExpiredSession()
      } else if (shouldShowPrompt) {
        handlePrompt()
      } else {
        sessionBroadcast.broadcastChannel.postMessage({ type: SESSION_UPDATE, timestamps })
        handleUpdateSession()
      }
    }, IDLE_MONITOR_INTERVAL)
  }

  useEffect(() => {
    if (enableIdleMonitor && userId) {
      const initialExpiration = Date.now() + SESSION_TIMEOUT_DURATION
      logger(`Beginning session for user: ${userId} at ${initialExpiration}`)

      sessionBroadcast.init()
      sessionBroadcast.on('message', ({ data }) => {
        switch (data.type) {
          case SESSION_UPDATE:
            if (data.timestamps.lastActive > lastActiveRef.current) {
              logger('Another tab has activity, updating timestamps', 'SESSION BROADCAST')
              handleUpdateSession(data.timestamps)
            } else {
              logger('No new activity from another tab, ignoring', 'SESSION BROADCAST')
            }
            break
          case SESSION_LOGOUT:
            logger('Another tab has logged out, clearing session', 'SESSION BROADCAST')
            handleExpiredSession()
            break
          default:
            break
        }
      })

      startTimer()
    } else if (enableIdleMonitor && !userId) {
      logger('No user ID found, clearing idle monitor')
      clearAll()
    } else {
      logger('Idle monitor is disabled', 'IDLE MONITOR', true)
    }

    return () => {
      clearAll()
    }
  }, [enableIdleMonitor, userId])

  return null
}
